/** @format */

/* eslint require-await: "warn" */

/* eslint react-hooks/rules-of-hooks: "warn" */
import {
    BaseIconProps,
    ButtonIconProps,
} from '@mpb-com/feather/components/atoms/icon';
import { Trans, useTranslation } from 'next-i18next';
import React, { FunctionComponent, useContext } from 'react';

import { MarketContext } from '@toucan/client/global/market';
import { WrappedLink } from '@toucan/client/global/nextjs';
import { UserContext } from '@toucan/client/global/user';

import { ToggleFavourite } from '../toggle-favourite';
import {
    getButtonLabel,
    getSavedModelsUrl,
} from './favourite-model-button.helpers';

export type FavouriteModelButtonProps = {
    isLoading?: boolean;
    modelId?: number;
} & (BaseIconProps | ButtonIconProps);

export const FavouriteModelButton: FunctionComponent<
    FavouriteModelButtonProps
> = ({ isLoading, modelId, ...iconProps }: FavouriteModelButtonProps) => {
    if (!isLoading && !modelId) {
        return null;
    }

    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);
    const { addSavedModel, removeSavedModel, isSavedModel } =
        useContext(UserContext);

    const isSaved = modelId ? isSavedModel(modelId) : false;

    async function handleToggleSaveModel() {
        if (!modelId) {
            return;
        }

        if (isSaved) {
            removeSavedModel(modelId);
            return;
        }

        addSavedModel(modelId);
    }

    const renderTooltipSuccessContent = () => {
        const successComponents = [
            <WrappedLink
                key={0}
                href="/account/saved-models"
                passHref={true}
                as={getSavedModelsUrl({ activeMarket, t })}
            >
                $1
            </WrappedLink>,
        ];

        return (
            <Trans components={successComponents}>
                {t('Model saved. <0>See all your saved models</0>.', {
                    context: 'favouriteModel',
                })}
            </Trans>
        );
    };

    return (
        <ToggleFavourite
            onToggle={handleToggleSaveModel}
            isFavourite={isSaved}
            successMessage={renderTooltipSuccessContent()}
            label={getButtonLabel({ isSaved, t })}
            isLoading={isLoading}
            {...iconProps}
        />
    );
};
